import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapPin, ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDebounce } from '../hooks/useDebounce';
import '../styles/LocationSearchBar.css';

/**
 * Location search bar component for province and city selection
 * Adapted from ActionSearchBar for location-specific use
 */
export default function LocationSearchBar({
  onProvinceSelect,
  onCitySelect,
  selectedProvince,
  selectedCity,
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchType, setSearchType] = useState('province'); // 'province' or 'city'
  const [searchTerm, setSearchTerm] = useState('');
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const debouncedSearchTerm = useDebounce(searchTerm, 200);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch provinces on mount
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const response = await fetch('/api/locations/provinces');
        const data = await response.json();
        if (data.provinces) {
          setProvinces(data.provinces);
        }
      } catch (error) {
        console.error('خطا در دریافت استان‌ها:', error);
      }
    };
    fetchProvinces();
  }, []);

  // Fetch cities when province changes
  useEffect(() => {
    if (!selectedProvince) {
      setCities([]);
      return;
    }

    const fetchCities = async () => {
      try {
        const response = await fetch(`/api/locations/cities?provinceId=${selectedProvince}`);
        const data = await response.json();
        if (data.cities) {
          setCities(data.cities);
        }
      } catch (error) {
        console.error('خطا در دریافت شهرها:', error);
      }
    };
    fetchCities();
  }, [selectedProvince]);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    const items = searchType === 'province' ? provinces : cities.map(city => ({ name: city }));

    if (!debouncedSearchTerm.trim()) {
      return items;
    }

    const normalizedSearch = debouncedSearchTerm.trim().toLowerCase();
    return items.filter(item => {
      const name = typeof item === 'string' ? item : item.name;
      return name.toLowerCase().includes(normalizedSearch);
    });
  }, [searchType, provinces, cities, debouncedSearchTerm]);

  // Handle item selection
  const handleSelectItem = useCallback((item) => {
    if (searchType === 'province') {
      onProvinceSelect(item.id);  // Pass province ID, not entire object
    } else {
      onCitySelect(item.name);
    }
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(0);
  }, [searchType, onProvinceSelect, onCitySelect]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < filteredItems.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : filteredItems.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex]) {
            handleSelectItem(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setSearchTerm('');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedIndex, handleSelectItem]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset selected index when filtered items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems]);

  // Get display label (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const getDisplayLabel = () => {
    if (searchType === 'province') {
      if (selectedProvince) {
        const province = provinces.find(p => p.id === selectedProvince);
        return province ? province.name : 'انتخاب استان';
      }
      return 'انتخاب استان';
    } else {
      return selectedCity || 'انتخاب شهر';
    }
  };

  const openProvinceSearch = () => {
    if (disabled) return;
    setSearchType('province');
    setSearchTerm('');
    setIsOpen(true);
  };

  const openCitySearch = () => {
    if (disabled || !selectedProvince) return;
    setSearchType('city');
    setSearchTerm('');
    setIsOpen(true);
  };

  const clearProvince = (e) => {
    e.stopPropagation();
    onProvinceSelect(null);
    onCitySelect('');
  };

  const clearCity = (e) => {
    e.stopPropagation();
    onCitySelect('');
  };

  return (
    <div className="location-search-container" ref={containerRef}>
      <div className="location-search-buttons">
        {/* City Button */}
        <button
          type="button"
          className={`location-search-button ${searchType === 'city' && isOpen ? 'active' : ''} ${!selectedProvince ? 'disabled' : ''}`}
          onClick={openCitySearch}
          disabled={disabled || !selectedProvince}
        >
          <MapPin size={16} />
          <span>{selectedCity || 'انتخاب شهر'}</span>
          {selectedCity ? (
            <X
              size={16}
              className="location-search-clear"
              onClick={clearCity}
            />
          ) : (
            <ChevronDown size={16} />
          )}
        </button>

        {/* Province Button */}
        <button
          type="button"
          className={`location-search-button ${searchType === 'province' && isOpen ? 'active' : ''}`}
          onClick={openProvinceSearch}
          disabled={disabled}
        >
          <MapPin size={16} />
          <span>{provinces.find(p => p.id === selectedProvince)?.name || 'انتخاب استان'}</span>
          {selectedProvince ? (
            <X
              size={16}
              className="location-search-clear"
              onClick={clearProvince}
            />
          ) : (
            <ChevronDown size={16} />
          )}
        </button>
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="location-search-dropdown"
          >
            {/* Search Input */}
            <div className="location-search-input-wrapper">
              <MapPin size={16} className="location-search-icon" />
              <input
                ref={inputRef}
                type="text"
                className="location-search-input"
                placeholder={searchType === 'province' ? 'جستجوی استان...' : 'جستجوی شهر...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="location-search-input-clear"
                  onClick={() => setSearchTerm('')}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Items List */}
            <div className="location-search-items">
              {filteredItems.length === 0 ? (
                <div className="location-search-empty">
                  نتیجه‌ای یافت نشد
                </div>
              ) : (
                filteredItems.map((item, index) => {
                  const displayName = typeof item === 'string' ? item : item.name;
                  const isSelected = index === selectedIndex;

                  return (
                    <motion.button
                      key={typeof item === 'string' ? item : (item.id || item.name)}
                      type="button"
                      className={`location-search-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectItem(item)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                    >
                      <MapPin size={14} />
                      <span>{displayName}</span>
                    </motion.button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="location-search-footer">
              <span className="location-search-hint">
                استفاده از ↑↓ برای حرکت، Enter برای انتخاب، Esc برای بستن
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
